// lib/dateUtils.ts

import { differenceInBusinessDays, format, isWeekend, parseISO, formatISO } from "date-fns";
import { toZonedTime, format as formatTZ } from "date-fns-tz";

export const calculateWorkingDays = (
  startDate: Date,
  endDate: Date,
): number => {
  // Handle same day case
  if (startDate.getTime() === endDate.getTime()) {
    return isWeekend(startDate) ? 0 : 1;
  }

  // Use date-fns to calculate business days (excluding weekends)
  return differenceInBusinessDays(endDate, startDate) + 1;
};

export const isHoliday = (date: Date): boolean => {
  // This is a placeholder for holiday checking logic
  // You would want to implement this based on your holiday calendar
  return false;
};

export const formatDateToBritish = (isoDate: string) => {
  return format(new Date(isoDate), "dd/MM/yyyy");
};

/**
 * Formats a UTC ISO date string to British format (DD/MM/YYYY) 
 * while preserving the UTC time without timezone conversion
 * @param utcDateString ISO date string with UTC time (e.g. '2025-03-01T00:00:00Z')
 * @returns Formatted date string in British format (DD/MM/YYYY)
 */
export const formatUTCDateToBritish = (utcDateString: string) => {
  // If null or undefined, return an empty string
  if (!utcDateString) return "";
  
  try {
    // Use parseISO from date-fns which properly handles UTC time strings
    const date = parseISO(utcDateString);
    
    // Format the date in British format - using UTC date explicitly
    return formatTZ(date, "dd/MM/yyyy", { timeZone: 'UTC' });
  } catch (error) {
    console.error("Error formatting UTC date:", error);
    return "";
  }
};

/**
 * Parses a UTC ISO date string to a Date object
 * while preserving the UTC time without timezone conversion
 * @param utcDateString ISO date string with UTC time (e.g. '2025-03-01T00:00:00Z')
 * @returns Date object representing the UTC date
 */
export const parseUTCDate = (utcDateString: string): Date | null => {
  if (!utcDateString) return null;
  
  try {
    // Parse the ISO string and explicitly convert to UTC
    const date = parseISO(utcDateString);
    return toZonedTime(date, 'UTC');
  } catch (error) {
    console.error("Error parsing UTC date:", error);
    return null;
  }
};
